import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { CustomerObjectsService } from "../../../../../../categories/customer-objects/customer-objects.service";
import { CustomerObjectDeviceDto } from "../../../../../models/customerObject/customer-object-device";
import { DeviceStatus, RequestStatus, RequestType } from "../../../../../enums";
import { RequestsService } from "../../../../../services/requests.service";
import { Request } from "../../../../../models/request/request";
import { SecurityService } from "../../../../../../core/services/security.service";
import { DeviceModelsService } from "../../../../../services/device-models.service";
import { DeviceModelDto } from "../../../../../models/device/device-model-dto";
import { CreateCustomerObjectDeviceDto } from "../../../../../models/device/create-customer-object-device.dto";
import { NewRequestDevice } from "../../../../../models/request/new-request/new-request-device";
import { forkJoin } from "rxjs";
import { AppService } from "../../../../../../app.service";
import { DropDownFilterSettings } from "@progress/kendo-angular-dropdowns";
import { DialogService } from "@progress/kendo-angular-dialog";
import { DevicesHierarchicalSelectorComponent } from "../../../../devices-hierarchical-selector/devices-hierarchical-selector.component";

@Component({
	selector: 'request-page-customer-object-devices',
	templateUrl: './request-page-customer-object-devices.component.html',
	styleUrls: ['./request-page-customer-object-devices.component.scss',
		'../../../../../../../vendor/libs/angular2-ladda/angular2-ladda.scss',
		'../../../../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
	]
})
export class RequestPageCustomerObjectDevicesComponent implements OnInit, OnChanges {

	@Input() customerObjectId: number;
	@Input() contragentId: number;
	@Input() canCreateServiceRequest: boolean = true;

	dropDownFilterSettings: DropDownFilterSettings = { caseSensitive: false, operator: 'contains' };
	loading: boolean = false;
	cardOpened: boolean = false;
	createServiceRequestLoading: boolean = false;

	mainDevices: CustomerObjectDeviceDto[] = [];
	expendableDevices: CustomerObjectDeviceDto[] = [];
	customerObjectDevices: CustomerObjectDeviceDto[] = [];
	deviceModels: DeviceModelDto[] = [];

	constructor (
		private customerObjectsService: CustomerObjectsService,
		private deviceModelsService: DeviceModelsService,
		private requestsService: RequestsService,
		private securityService: SecurityService,
		private appService: AppService,
		private dialogService: DialogService
	) { }

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.customerObjectId || changes.contragentId) {
			this.initData();
		}
	}

	ngOnInit(): void {
		this.initData();
	}

	initData() {
		if (!this.customerObjectId) {
			this.customerObjectDevices = [];
		}
		else {
			this.loading = true;
			this.customerObjectsService.getCustomerObjectDevices(this.customerObjectId).subscribe(resp => {
				// INBWT-823: временно отключена фильтрация по IsMainDevice
				// this.mainDevices = resp.filter(x => x.isMainDevice);
				this.mainDevices = resp.filter(x => !x.isExpendable);
				this.expendableDevices = resp.filter(x => x.isExpendable);
				this.loading = false; 
			});
		}
		

		this.loading = true;
		forkJoin(
			this.customerObjectsService.getCustomerObjectDevices(this.customerObjectId),
			this.deviceModelsService.getAll()
		).subscribe(([customerObjectDevices, deviceModels]) => {
			this.customerObjectDevices = customerObjectDevices;
			this.customerObjectDevices.forEach(x => {
				x.installationDate = this.appService.parseDate(x.installationDate);
			});
			this.deviceModels = deviceModels.filter(x => x.parentId != null);
			this.loading = false;
		}, () => this.loading = false);
	}

	public rowClass() {
		return {
			'cursor-pointer': true
		};
	}

	createCustomerObjectDevice(): CustomerObjectDeviceDto {
		let device = new CustomerObjectDeviceDto();
		device.customerObjectId = this.customerObjectId;
		return device;
	}

	addCustomerObjectDevice() {
		const dialogRef = this.dialogService.open({
			content: DevicesHierarchicalSelectorComponent,
			title: 'Добавление оборудования',
			width: '80%',
			height: '870px',
			minWidth: '800px'
		});
		const devicesSelector = <DevicesHierarchicalSelectorComponent>dialogRef.content.instance;

		devicesSelector.height = 500;
		devicesSelector.isReclamation = true;

		devicesSelector.onCancel.subscribe(() => {
			dialogRef.close();
		});
		devicesSelector.onContinue.subscribe((requestDevices: NewRequestDevice[]) => {
			dialogRef.close();

			const devices = this.createCustomerObjectDevices(requestDevices);
			if (devices.length == 0) {
				return;
			}

			this.loading = true;
			const createDevices$ = devices.map(device => {
				return this.customerObjectsService.createCustomerObjectDevice(this.customerObjectId, device);
			});

			forkJoin(createDevices$).subscribe(() => {
				this.initData();
			}, () => this.loading = false);
		});
	}

	private createCustomerObjectDevices(requestDevices: NewRequestDevice[]): CreateCustomerObjectDeviceDto[] {
		const devices: CreateCustomerObjectDeviceDto[] = [];

		requestDevices.forEach(requestDevice => {
			const quantity = requestDevice.quantity > 0 ? requestDevice.quantity : 1;

			for (let i = 0; i < quantity; i++) {
				devices.push(this.createCustomerObjectDeviceFromRequestDevice(requestDevice));
			}
		});

		return devices;
	}

	private createCustomerObjectDeviceFromRequestDevice(requestDevice: NewRequestDevice): CreateCustomerObjectDeviceDto {
		const deviceModel = this.getSelectedDeviceModel(requestDevice.deviceModelId);
		const device = new CreateCustomerObjectDeviceDto();

		device.deviceModelId = requestDevice.deviceModelId;
		device.deviceTypeId = requestDevice.deviceTypeId || (deviceModel ? deviceModel.typeId : null);
		device.deviceModelName = requestDevice.deviceModelName || (deviceModel ? deviceModel.name : null);
		device.deviceStatusId = <number>DeviceStatus.installed;
		device.ownerContragentId = this.contragentId;
		device.requestCategoryId = deviceModel ? deviceModel.requestCategoryId : null;

		return device;
	}

	saveCustomerObjectDevice({ dataItem, isNew }) {
		const deviceModel = this.getSelectedDeviceModel(dataItem.deviceModelId);

		if (deviceModel == null) {
			return;
		}

		dataItem.deviceTypeId = deviceModel.typeId;
		dataItem.deviceModelName = deviceModel.name;
		dataItem.deviceSupplierId = deviceModel.parentId;
		dataItem.requestCategoryId = deviceModel.requestCategoryId;

		this.loading = true;

		if (isNew || !dataItem.customerObjectDeviceId) {
			const device = new CreateCustomerObjectDeviceDto();
			device.deviceTypeId = deviceModel.typeId;
			device.deviceModelId = deviceModel.id;
			device.deviceModelName = deviceModel.name;
			device.serialNumber = dataItem.serialNumber;
			device.deviceStatusId = <number>DeviceStatus.installed;
			device.ownerContragentId = this.contragentId;
			device.requestCategoryId = deviceModel.requestCategoryId;
			device.installationDate = dataItem.installationDate;

			this.customerObjectsService.createCustomerObjectDevice(this.customerObjectId, device)
				.subscribe(() => this.initData(), () => this.loading = false);
			return;
		}

		this.customerObjectsService.updateCustomerObjectDevice(dataItem)
			.subscribe(() => this.initData(), () => this.loading = false);
	}

	removeCustomerObjectDevice({ dataItem }) {
		this.loading = true;
		this.customerObjectsService.deleteCustomerObjectDevice(dataItem.customerObjectDeviceId)
			.subscribe(() => this.initData(), () => this.loading = false);
	}

	editCustomerObjectDeviceHandler({ sender, rowIndex, dataItem }) {
		dataItem.installationDate = this.appService.parseDate(dataItem.installationDate);
		sender.editRow(rowIndex);
	}

	canSaveCustomerObjectDevice(device: CustomerObjectDeviceDto): boolean {
		const deviceModel = this.getSelectedDeviceModel(device.deviceModelId);

		return this.customerObjectId != null &&
			this.contragentId != null &&
			deviceModel != null &&
			deviceModel.typeId != null;
	}

	private getSelectedDeviceModel(deviceModelId: number): DeviceModelDto {
		return this.deviceModels.find(x => x.id == deviceModelId);
	}

	toggleCard() {
		this.cardOpened = !this.cardOpened;
	}

	createServiceRequest() {
		this.createServiceRequestLoading = true;

		let newRequest = new Request;
		newRequest.customerContragentId = this.contragentId;
		newRequest.customerObjectId = this.customerObjectId;
		newRequest.performerUserId = this.securityService._currentUser.userId;
		newRequest.requestTypeId = <number>RequestType.service;
		newRequest.statusId = <number>RequestStatus.InProgress;

		this.requestsService.create(newRequest).subscribe((resp) => {
			window.open(`/requests/${resp.data}`, '_blank');
			this.createServiceRequestLoading = false;
		});
	}
}
